<template>
  <div
    :class="['zg-quoted-by', {
      [`zq-quoted-by--direction-${direction}`]: direction
    }]"
  >
    <div class="avatar avatar--desktop">
      <zg-avatar
        :image="image"
        :text="initials"
      />
    </div>

    <div class="content">
      <div class="header">
        <div class="avatar avatar--mobile">
          <zg-avatar
            :image="image"
            :text="initials"
          />
        </div>

        <div class="person">
          <a
            :href="bioLink"
            class="name"
          >
            <h4>{{ name }}</h4>

            <zg-icon
              v-if="bioLink"
              icon="arrow-forward"
            />
          </a>

          <h6 v-if="jobTitle">
            {{ jobTitle }}
          </h6>
        </div>
      </div>

      <div class="quote quote--mobile">
        <zg-quote
          :text="text"
          :direction="direction"
          :background="background"
        >
          <slot />
        </zg-quote>
      </div>

      <div class="quote quote--desktop">
        <zg-quote
          :text="text"
          :direction="desktopDirection"
          :background="background"
        >
          <slot />
        </zg-quote>
      </div>

      <div class="trustpilot">
        <zg-trustpilot
          v-if="trustpilotData"
          :stars="trustpilotData.stars"
          :inline-text="trustpilotData.scoreTemplate.replace(/<\/?[^>]+(>|$)/g, '')"
        />

        <div
          v-if="disclaimer && trustpilotData"
          class="disclaimer"
        >
          {{ disclaimer }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ZgAvatar } from '../ZgAvatar'
  import ZgIcon from '../ZgIcon/ZgIcon.vue'
  import ZgQuote from '../ZgQuote/ZgQuote'
  import ZgTrustpilot from '../ZgTrustpilot/ZgTrustpilot'

  export default {
    name: 'ZgQuotedBy',
    components: {
      ZgTrustpilot,
      ZgIcon,
      ZgQuote,
      ZgAvatar
    },
    props: {
      image: {
        type: String,
        required: false,
        default: null
      },
      name: {
        type: String,
        required: true
      },
      bioLink: {
        type: String,
        required: false,
        default: null
      },
      jobTitle: {
        type: String,
        required: false,
        default: null
      },
      text: {
        type: String,
        required: false,
        default: null
      },
      background: {
        type: String,
        required: false,
        default: 'beige',
        validator: (value) => [
          'beige',
          'secondary-500'
        ].includes(value)
      },
      trustpilotData: {
        type: Object,
        required: false,
        default: null
      },
      disclaimer: {
        type: String,
        required: false,
        default: null
      },
      direction: {
        type: String,
        required: false,
        default: 'bottom-left',
        validator: (value) => [
          'top-left',
          'top-right',
          'bottom-left',
          'bottom-right'
        ].includes(value)
      }
    },
    computed: {
      initials () {
        return this.name
          ?.split(' ')
          ?.map(n => n[0]).join('')
      },
      desktopDirection () {
        return this.direction?.includes('left') ? 'left-top' : 'right-top'
      }
    }
  }
</script>

<style lang="scss" scoped>
  .zg-quoted-by {
    display: flex;
    flex-direction: column;

    .content {
      display: flex;
      flex-direction: column;
      gap: rem(12px);
    }

    .header {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      justify-content: flex-start;
      gap: rem(12px);
      margin: 0 0 (- rem(12px)); // negate gap on mobile

      @include mq(medium) {
        align-items: center;
        margin: 0;
      }
    }

    .avatar {
      flex-shrink: 0;
      width: rem(56px);
      height: rem(56px);

      @include mq(medium) {
        width: rem(96px);
        height: rem(96px);
      }

      &--mobile {
        display: flex;

        @include mq(medium) {
          display: none;
        }
      }

      &--desktop {
        display: none;

        @include mq(medium) {
          display: flex;
        }
      }

      & > div {
        width: 100%;
        height: 100%;
      }
    }

    .person {
      margin: rem(4px) 0 0;

      @include mq(medium) {
        padding: 0 rem(32px);
        margin: 0;
      }

      h4 {
        @include type-title-s;

        margin: 0;
      }

      h6 {
        @include type-body-s;

        margin: 0;
      }

      .name {
        display: flex;
        align-items: center;
        column-gap: rhythm(xsmall);
        border-bottom: none;
        cursor: pointer;
      }
    }

    .quote {
      &--mobile {
        display: flex;

        @include mq(medium) {
          display: none;
        }
      }

      &--desktop {
        display: none;

        @include mq(medium) {
          display: flex;
        }
      }
    }

    .trustpilot {
      display: flex;
      flex-direction: column;
      gap: rem(12px);
      padding: 0 rem(24px);

      @include mq(medium) {
        padding: 0 rem(32px);
      }

      .disclaimer {
        @include type-body-m;

        margin: 0;
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // directions
  // ? ----------------------------------------------------------------------

  .zq-quoted-by--direction-top-left,
  .zq-quoted-by--direction-bottom-left {
    @include mq(medium) {
      flex-direction: row;
    }

    .header {
      @include mq(medium) {
        justify-content: flex-start;
      }
    }

    .person,
    .trustpilot {
      @include mq(medium) {
        padding-left: rem(64px)
      }
    }

    .person {
      align-items: flex-start;

      @include mq(medium) {
        align-items: flex-end;
      }
    }
  }

  .zq-quoted-by--direction-top-right,
  .zq-quoted-by--direction-bottom-right {
    @include mq(medium) {
      flex-direction: row-reverse;
    }

    .header {
      flex-direction: row-reverse;

      @include mq(medium) {
        justify-content: flex-start;
      }
    }

    .person,
    .trustpilot {
      @include mq(medium) {
        padding-right: rem(64px);
      }
    }

    .person {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      text-align: right;
      align-items: flex-end;

      @include mq(medium) {
        align-items: flex-end;
      }
    }
  }

  .zq-quoted-by--direction-bottom-left,
  .zq-quoted-by--direction-bottom-right {
    .content {
      flex-direction: column-reverse;

      @include mq(medium) {
        flex-direction: column;
      }
    }
  }
</style>
